// Двухэкранный диалог: форма создания → экран выдачи секретов.
// Re-auth через поле «Ваш пароль» (SPEC §8.3). Секреты (otpauth_url +
// recovery_codes) показываются один раз; UI обязан подчеркнуть это.

import { useState, type FormEvent } from 'react';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';
import { ApiError } from '../../lib/api.ts';
import { useCreateUser } from '../../lib/hooks.ts';
import type { CreatedUser } from '../../lib/types.ts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PASSWORD_MIN = 12;

export function AddUserDialog({ open, onOpenChange }: Props): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [role, setRole] = useState<'owner' | 'admin'>('admin');
  const [requesterPassword, setRequesterPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedUser | null>(null);
  const createMutation = useCreateUser();

  function reset(): void {
    setEmail('');
    setPassword('');
    setPasswordConfirm('');
    setRole('admin');
    setRequesterPassword('');
    setError(null);
    setCreated(null);
  }

  function handleClose(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== passwordConfirm) {
      setError('Пароли не совпадают');
      return;
    }
    if (password.length < PASSWORD_MIN) {
      setError(`Пароль должен быть от ${PASSWORD_MIN} символов`);
      return;
    }
    try {
      const result = await createMutation.mutateAsync({
        email: email.trim().toLowerCase(),
        password,
        role,
        requester_password: requesterPassword,
      });
      setCreated(result);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'reauth_failed') {
          setError('Ваш пароль введён неверно');
        } else if (err.code === 'email_exists') {
          setError('Пользователь с таким email уже существует');
        } else if (err.code === 'weak_password') {
          setError(`Пароль должен быть от ${PASSWORD_MIN} символов`);
        } else if (err.status === 422) {
          setError('Неверные данные. Проверь email и поля формы.');
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось создать пользователя');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        {!created ? (
          <form onSubmit={onSubmit}>
            <DialogTitle>Новый пользователь</DialogTitle>
            <DialogDescription>
              Сразу после создания будут показаны TOTP-секрет и recovery-коды. Их нужно сохранить и
              передать новому пользователю — повторно показаны не будут.
            </DialogDescription>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={320}
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="user-password">Пароль</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={PASSWORD_MIN}
                  maxLength={256}
                />
                <p className="text-xs text-fg-muted">Минимум {PASSWORD_MIN} символов.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="user-password-confirm">Пароль ещё раз</Label>
                <Input
                  id="user-password-confirm"
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  minLength={PASSWORD_MIN}
                  maxLength={256}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="user-role">Роль</Label>
                <select
                  id="user-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'owner' | 'admin')}
                  className="h-9 w-full rounded border border-border bg-bg px-2 text-sm"
                >
                  <option value="admin">admin</option>
                  <option value="owner">owner</option>
                </select>
                <p className="text-xs text-fg-muted">
                  На MVP роли не разграничивают права — поле задел.
                </p>
              </div>

              <div className="space-y-1.5 rounded border border-border bg-bg-soft p-3">
                <Label htmlFor="requester-password">Ваш пароль для подтверждения</Label>
                <Input
                  id="requester-password"
                  type="password"
                  value={requesterPassword}
                  onChange={(e) => setRequesterPassword(e.target.value)}
                  required
                  maxLength={256}
                />
                <p className="text-xs text-fg-muted">
                  Защита от создания юзера через захваченную сессию.
                </p>
              </div>
            </div>

            {error ? (
              <p className="mt-3 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
                {error}
              </p>
            ) : null}

            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
                Отмена
              </Button>
              <Button type="submit" variant="primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Создаём…' : 'Создать'}
              </Button>
            </div>
          </form>
        ) : (
          <CreatedSecretsView
            email={created.user.email}
            otpauthUrl={created.otpauth_url}
            recoveryCodes={created.recovery_codes}
            onClose={() => handleClose(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreatedSecretsView({
  email,
  otpauthUrl,
  recoveryCodes,
  onClose,
}: {
  email: string;
  otpauthUrl: string;
  recoveryCodes: string[];
  onClose: () => void;
}): JSX.Element {
  return (
    <div>
      <DialogTitle>Пользователь {email} создан</DialogTitle>
      <DialogDescription>
        Передай эти данные новому пользователю. Повторно мы их не покажем — если потеряешь, юзера
        придётся пересоздать.
      </DialogDescription>

      <div className="mb-4 flex items-start gap-2 rounded border border-danger/40 bg-danger/5 p-3 text-sm text-danger">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>Сохрани сейчас. После закрытия окна секреты исчезнут.</span>
      </div>

      <SecretBlock label="otpauth URL (для импорта в Authenticator)" value={otpauthUrl} />

      <div className="mt-4">
        <Label>Recovery-коды (8 штук, одноразовые)</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2 rounded border border-border bg-bg p-3">
          {recoveryCodes.map((code) => (
            <code key={code} className="font-mono text-xs">
              {code}
            </code>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="primary" onClick={onClose}>
          Готово
        </Button>
      </div>
    </div>
  );
}

function SecretBlock({ label, value }: { label: string; value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label>{label}</Label>
        <button
          onClick={() => void copy()}
          className="flex items-center gap-1 text-xs text-fg-muted hover:text-accent"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Скопировано' : 'Скопировать'}
        </button>
      </div>
      <code className="block break-all rounded border border-border bg-bg p-3 font-mono text-xs">
        {value}
      </code>
    </div>
  );
}
